import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';
import { Target, Search, Plus, Map, Send, Clock, PlayCircle, Archive, Trash2, Edit3, RotateCcw, MoreVertical } from 'lucide-react';

export default function PrimeProjectList() {
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('ACTIVE');
  const [showMenuId, setShowMenuId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_URL}/brand-projects?system=geo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        // Only show projects that might have PRIME seeds or are just system=geo
        setProjects(data.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const setGlobalAndNavigate = (projectId, path) => {
    localStorage.setItem('globalProject', projectId);
    window.dispatchEvent(new Event('globalFilterChanged'));
    navigate(path);
  };

  const handleStatusChange = async (id, newStatus) => {
     try {
        const token = localStorage.getItem('token') || '';
        const res = await fetch(`${API_URL}/brand-projects/${id}/status`, {
           method: 'PATCH',
           headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
           body: JSON.stringify({ status: newStatus })
        });
        const data = await res.json();
        if (data.success) {
           fetchProjects();
           setShowMenuId(null);
        } else {
           alert("操作失败");
        }
     } catch(e) {
        console.error(e);
     }
  };

  const handleDelete = async (id) => {
     if (!window.confirm("确定要彻底销毁该战略项目吗？此操作不可逆！")) return;
     try {
        const token = localStorage.getItem('token') || '';
        const res = await fetch(`${API_URL}/brand-projects/${id}/status`, {
           method: 'DELETE',
           headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
           fetchProjects();
        } else {
           alert("删除失败");
        }
     } catch(e) {
        console.error(e);
     }
  };

  const filteredProjects = projects.filter(p => {
      const s = p.status || 'ACTIVE';
      return s === activeTab;
  });


  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Target className="w-8 h-8 text-blue-500" />
            战略项目矩阵 (PRIME Projects)
          </h1>
          <p className="text-slate-500 mt-2">管理所有的意图作战计划，记录全局唯一的任务调度号 (Task No.)。</p>
        </div>
        <button 
          onClick={() => navigate('/prime/wizard')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          新建 PRIME 战略部署
        </button>
      </div>
      
      <div className="flex items-center gap-6 border-b border-slate-200 dark:border-slate-700 mb-6">
        {[
          { id: 'ACTIVE', label: '活跃战略' },
          { id: 'ARCHIVED', label: '已归档' },
          { id: 'DELETED', label: '回收站' }
        ].map(tab => (
           <button
             key={tab.id}
             onClick={() => setActiveTab(tab.id)}
             className={`pb-3 px-2 text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
           >
             {tab.label}
           </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          <Target className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white">暂无战略项目</h3>
          <p className="text-slate-500 mt-2 mb-6">您还没有创建任何 PRIME 战略部署，立即去向导里建一个吧。</p>
          <button onClick={() => navigate('/prime/wizard')} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow font-medium">新建战略</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredProjects.map(proj => (
            <div key={proj.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center border border-blue-100 dark:border-blue-800/50">
                  {proj.logo ? <img src={proj.logo} alt={proj.name} className="w-8 h-8 object-contain" /> : <span className="font-bold text-blue-600 dark:text-blue-400 text-lg">{proj.name.substring(0, 1)}</span>}
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">{proj.name}</h3>
                    <span className="px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-mono font-medium border border-slate-200 dark:border-slate-600">
                      Task No: PRJ-{proj.id}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-4">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> 创建于 {new Date(proj.createdAt).toLocaleDateString()}</span>
                    <span>竞品: {proj.competitors ? proj.competitors.length : 0} 个</span>
                    <span>研究种子: {proj.PromptResearchSeed ? proj.PromptResearchSeed.length : 0} 组</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto relative">
                {activeTab === 'ACTIVE' && (
                  <>
                    <button 
                      onClick={() => setGlobalAndNavigate(proj.id, '/prime/matrix')}
                      className="flex-1 md:flex-none px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 dark:text-indigo-300 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 border border-indigo-200 dark:border-indigo-800/50"
                    >
                      <Map className="w-4 h-4" /> 全息意图矩阵
                    </button>
                    <button 
                      onClick={() => setGlobalAndNavigate(proj.id, '/prime/dispatch')}
                      className="flex-1 md:flex-none px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 dark:text-emerald-300 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 border border-emerald-200 dark:border-emerald-800/50"
                    >
                      <PlayCircle className="w-4 h-4" /> 拓词调度舱
                    </button>
                    <div className="relative">
                       <button onClick={() => setShowMenuId(showMenuId === proj.id ? null : proj.id)} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                         <MoreVertical className="w-5 h-5" />
                       </button>
                       {showMenuId === proj.id && (
                         <>
                           <div className="fixed inset-0 z-10" onClick={() => setShowMenuId(null)}></div>
                           <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden z-20 py-1">
                             <button onClick={() => navigate(`/prime/wizard?id=${proj.id}`)} className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-200"><Edit3 className="w-4 h-4 text-slate-500" /> 编辑配置</button>
                             <button onClick={() => handleStatusChange(proj.id, 'ARCHIVED')} className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-200"><Archive className="w-4 h-4 text-slate-500" /> 归档项目</button>
                             <div className="h-px bg-slate-100 dark:bg-slate-700 my-1"></div>
                             <button onClick={() => handleStatusChange(proj.id, 'DELETED')} className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"><Trash2 className="w-4 h-4" /> 移至回收站</button>
                           </div>
                         </>
                       )}
                    </div>
                  </>
                )}
                
                {activeTab === 'ARCHIVED' && (
                  <>
                    <button onClick={() => handleStatusChange(proj.id, 'ACTIVE')} className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium text-sm flex items-center gap-2"><RotateCcw className="w-4 h-4" /> 恢复至活跃</button>
                    <button onClick={() => handleStatusChange(proj.id, 'DELETED')} className="px-4 py-2 bg-red-50 text-red-600 rounded-lg font-medium text-sm flex items-center gap-2"><Trash2 className="w-4 h-4" /> 移至回收站</button>
                  </>
                )}

                {activeTab === 'DELETED' && (
                  <>
                    <button onClick={() => handleStatusChange(proj.id, 'ACTIVE')} className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium text-sm flex items-center gap-2"><RotateCcw className="w-4 h-4" /> 还原项目</button>
                    <button onClick={() => handleDelete(proj.id)} className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium text-sm flex items-center gap-2"><Trash2 className="w-4 h-4" /> 彻底销毁</button>
                  </>
                )}

                
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
